<script setup>
// Imports
import 'vue-json-pretty/lib/styles.css'

import DialogBar from 'components/DialogBar.vue'
import DialogFooter from 'components/UI/Dialogs/DialogFooter.vue'
import OmniListFromArray from 'components/UI/OmniListFromArray.vue'
import VueJsonPretty from 'vue-json-pretty'

// Dialog essentials
defineEmits([...useDialogPluginComponent.emits])
const { dialogRef, onDialogHide, onDialogCancel } = useDialogPluginComponent()

// Component props
const componentProps = defineProps({
  dialogIcon: { type: String, default: null },
  dialogSubtitle: { type: String, default: null },
  propItem: {
    type: Object,
    required: true,
  },
})

// Constants
const $q = useQuasar()

// Refs
const maximizedToggle = ref(false)
const item = ref(componentProps.propItem)

// Computed properties
const maximized = computed(() => {
  return maximizedToggle.value || $q.screen.lt.md
})

// Methods
/**
 * Create usable array from mixed data for sms addresses
 * @param data
 * @returns {*[]}
 */
const listFromAddresses = (data) => {
  if (!item.value) {
    return []
  }

  const arr = []

  for (const entry of data) {
    if (typeof entry === 'object') {
      arr.push(`${entry.name} <${entry.address}>`)
    } else {
      arr.push(entry)
    }
  }

  return arr
}

// Watchers
</script>

<template>
  <q-dialog ref="dialogRef" :maximized="maximized" persistent @hide="onDialogHide">
    <q-card :class="!maximized ? 'dialog-width-75' : ''" class="relative-position">
      <div id="request-trace" class="relative-position">
        <dialog-bar
          :icon="dialogIcon || 'sym_o_visibility'"
          :subtitle="dialogSubtitle"
          v-model:maximized-toggler="maximizedToggle"
          :maximized="maximized"
          :dialog-type="$attrs.type"
          items-service="sms-logs"
          maximize-button
        ></dialog-bar>
        <q-separator />
        <q-card-section :class="!maximized ? 'dialog-normal' : 'dialog-maximized'" class="scroll q-pa-md relative-position">
          <q-markup-table bordered flat separator="cell">
            <tbody>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.CreatedAt') }}
                </q-td>
                <q-td>
                  {{
                    $d(
                      item.createdAt,
                      {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      },
                      useUserStore().currentRegionalSettings.lang,
                    )
                  }}
                </q-td>
              </q-tr>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.To') }}
                </q-td>
                <q-td>
                  <omni-list-from-array :list-items="listFromAddresses(item.to)" no-padding bullets />
                </q-td>
              </q-tr>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.SmsSent') }}
                </q-td>
                <q-td>
                  <q-icon :name="item.smsSent ? 'o_done' : 'o_clear'" :class="item.smsSent ? 'text-positive' : 'text-negative'" />
                </q-td>
              </q-tr>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.SmsSentAt') }}
                </q-td>
                <q-td>
                  {{
                    item.smsSentAt
                      ? $d(
                          item.smsSentAt,
                          {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          },
                          useUserStore().currentRegionalSettings.lang,
                        )
                      : '-'
                  }}
                </q-td>
              </q-tr>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.Content') }}
                </q-td>
                <q-td>
                  {{ item.content }}
                </q-td>
              </q-tr>
              <q-tr>
                <q-td id="horizontal-header" class="text-weight-bolder bg-grey-2" auto-width>
                  {{ $t($frontendType + '.Sms-logs.VendorResponse') }}
                </q-td>
                <q-td>
                  <vue-json-pretty :virtual="true" v-if="item.vendorResponse" :data="item.vendorResponse"></vue-json-pretty>
                  <template v-else>-</template>
                </q-td>
              </q-tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
        <q-card-actions align="right">
          <DialogFooter>
            <template #footer-info-top-right> &nbsp;</template>
            <template #footer-buttons>
              <q-btn color="negative" :label="$t('Shared.Globals.Close')" class="btn-min-width-100" @click="onDialogCancel" />
            </template>
          </DialogFooter>
        </q-card-actions>
      </div>
    </q-card>
  </q-dialog>
</template>
